import { useEffect, useMemo, useState } from 'react';
import { mapCadSysteemType, type CadCollectorLine, type CadJsonPayload, type CadSysteemEnum } from '@/api/cadJson';
import { createCollector } from '@/api/tasks';
import type { CollectorPicking } from '@/api/tasks';
import type { Collector } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { VERDIEPING_KEYS, normaliseVerdieping } from '@/utils/verdieping';
import { findKringGaps, formatKringGaps } from '@/utils/kringGaps';
import { formatPickingOption } from '@/utils/collectors';
import { useCollectorSystems, useKringSystems } from '@/stores/systems';

// Strip file extensions and " - CSV" / " - JSON" label suffixes (case-insensitive)
// so payload.fileName matches carrier_tracking_ref regardless of how the picking
// was named (e.g. "S123 - Project - CSV" → "S123 - Project").
function normalizeCarrierRef(s: string): string {
  return s.trim()
    .replace(/\s+-\s+(csv|json)$/i, '')
    .replace(/\.(json|csv)$/i, '')
    .trim();
}

interface CadCollectorImportWizardProps {
  projectId: number;
  payload: CadJsonPayload;
  collectors: Collector[];
  pickings: CollectorPicking[] | null;
  pickingsLoading: boolean;
  existingBloks: string[];
  existingVerdiepingen: string[];
  onClose: () => void;
}

type WizardStep = 'checklist' | 'form';

export function CadCollectorImportWizard({
  projectId,
  payload,
  collectors,
  pickings,
  pickingsLoading,
  existingBloks,
  existingVerdiepingen,
  onClose,
}: CadCollectorImportWizardProps) {
  const fetchCollectoren = useProjectViewStore((s) => s.fetchCollectoren);
  const [step, setStep] = useState<WizardStep>('checklist');
  const [selected, setSelected] = useState<Set<number>>(() => new Set(payload.collectoren.map((_, index) => index)));
  const [currentPosition, setCurrentPosition] = useState(0);
  const [allowNoMatch, setAllowNoMatch] = useState(false);
  // Ack for the "no materiaallijst exists at all yet" escape — required before the
  // "Doorgaan" button unlocks (mirrors DashboardCollectorCreateForm's checkbox).
  const [ackNoPickings, setAckNoPickings] = useState(false);
  // When materiaallijsten exist but none auto-matches the CAD filename, the user picks
  // one here that applies to every imported collector (pre-fills the per-collector select,
  // still editable). Removes the old bare "Toch doorgaan" unlinked escape.
  const [wizardPickingId, setWizardPickingId] = useState<number | null>(null);
  const selectedIndexes = useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected]);
  const currentCollector = step === 'form' ? payload.collectoren[selectedIndexes[currentPosition]] : null;

  // Match pickings by normalized filename. Determines whether we can proceed.
  const normalizedFileName = normalizeCarrierRef(payload.fileName);
  const matchedPickings = useMemo(
    () => (pickings ?? []).filter(p => p.carrier_tracking_ref !== null && normalizeCarrierRef(p.carrier_tracking_ref) === normalizedFileName),
    [pickings, normalizedFileName],
  );
  const lockedPickingId = matchedPickings.length === 1 ? matchedPickings[0].id : null;

  // Collectors whose kring sequence has gaps → the AutoCAD extraction dropped a
  // kring. Surface this BEFORE import so it's caught at the moment it happens.
  const collectorsWithGaps = useMemo(
    () => payload.collectoren
      .map((c) => ({ collector: c, gaps: findKringGaps(c.kringen.map((k) => k.kringNr)) }))
      .filter((entry) => entry.gaps.length > 0),
    [payload.collectoren],
  );

  function startImport() {
    if (selectedIndexes.length === 0) return;
    setCurrentPosition(0);
    setStep('form');
  }

  function handleSaved() {
    const next = currentPosition + 1;
    if (next >= selectedIndexes.length) {
      fetchCollectoren(projectId);
      onClose();
      return;
    }
    setCurrentPosition(next);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-5xl rounded-xl bg-white shadow-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-td-navy">Collectoren importeren vanuit CAD</h3>
            <p className="text-xs text-td-muted mt-0.5">{payload.fileName}</p>
          </div>
          <button type="button" onClick={onClose} className="text-td-muted hover:text-td-navy" aria-label="Sluiten">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {payload.warning && (
          <div className="mx-5 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
            <strong className="font-semibold">Let op:</strong> {payload.warning.message}
          </div>
        )}

        {collectorsWithGaps.length > 0 && (
          <div className="mx-5 mt-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            <strong className="font-semibold">⚠ Ontbrekende kringen:</strong> de CAD-extractie heeft mogelijk kringen gemist.
            Controleer de extractie voor:
            <ul className="mt-1 list-disc pl-5 space-y-0.5">
              {collectorsWithGaps.map(({ collector, gaps }) => (
                <li key={collector.collNr}>
                  <span className="font-semibold">{defaultCollectorName(collector.collNr, collector.appartement)}</span>
                  {' — '}{formatKringGaps(gaps)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Guard: pickings not yet loaded */}
        {(pickingsLoading || pickings === null) ? (
          <div className="p-8 text-center text-sm text-td-muted">Materiaallijsten laden...</div>
        ) : matchedPickings.length === 0 && !allowNoMatch ? (
          /* No filename match — hard block only when there are truly no pickings at all */
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-td-navy mb-1">
              {pickings!.length === 0 ? 'Materiaallijst ontbreekt' : 'Geen overeenkomende materiaallijst'}
            </p>
            <p className="text-sm text-td-muted mb-1">
              {pickings!.length === 0
                ? 'Importeer eerst de materiaallijst voor dit project.'
                : 'Geen materiaallijst komt automatisch overeen met dit CAD bestand:'}
            </p>
            <p className="text-xs font-mono text-td-muted bg-gray-50 rounded px-2 py-1 inline-block mb-5">{payload.fileName}</p>
            {pickings!.length > 0 ? (
              /* Materiaallijsten exist but none matched: pick one for ALL these collectors. */
              <div className="mx-auto max-w-sm text-left mb-5">
                <label htmlFor="cad-wizard-materiaallijst" className="block text-xs font-semibold text-td-muted mb-1">
                  Selecteer de materiaallijst voor deze collectoren <span className="text-red-500">*</span>
                </label>
                <select
                  id="cad-wizard-materiaallijst"
                  value={wizardPickingId ?? ''}
                  onChange={(e) => setWizardPickingId(Number(e.target.value) || null)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue"
                >
                  <option value="" disabled>— Kies een materiaallijst —</option>
                  {pickings!.map((p) => (
                    <option key={p.id} value={p.id}>{formatPickingOption(p)}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-td-muted">Je kan dit per collector nog aanpassen in de volgende stap.</p>
              </div>
            ) : (
              <label className="mb-4 flex items-center justify-center gap-2 text-sm font-semibold text-td-navy">
                <input
                  type="checkbox"
                  checked={ackNoPickings}
                  onChange={(e) => setAckNoPickings(e.target.checked)}
                />
                Materiaallijst nog niet beschikbaar — later koppelen
              </label>
            )}
            <div className="flex justify-center gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy">
                Sluiten
              </button>
              {pickings!.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setAllowNoMatch(true)}
                  disabled={wizardPickingId === null}
                  className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Doorgaan
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setAllowNoMatch(true)}
                  disabled={!ackNoPickings}
                  className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50"
                >
                  Doorgaan
                </button>
              )}
            </div>
          </div>
        ) : matchedPickings.length > 1 ? (
          /* Guard: multiple matching materiaallijsten — ambiguous */
          <div className="p-8 text-center">
            <p className="text-sm font-semibold text-td-navy mb-1">Meerdere materiaallijsten gevonden</p>
            <p className="text-sm text-td-muted mb-3">Koppel de collectors handmatig via de materiaallijst dropdown.</p>
            <ul className="text-xs text-td-muted text-left inline-block mb-5 space-y-1">
              {matchedPickings.map(p => (
                <li key={p.id} className="font-mono bg-gray-50 rounded px-2 py-1">{p.name}{p.fase_name ? ` — ${p.fase_name}` : ''}</li>
              ))}
            </ul>
            <div className="flex justify-center">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy">
                Sluiten
              </button>
            </div>
          </div>
        ) : step === 'checklist' ? (
          <CollectorChecklist
            collectors={payload.collectoren}
            selected={selected}
            onToggle={(index) => {
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(index)) next.delete(index);
                else next.add(index);
                return next;
              });
            }}
            onStart={startImport}
            onClose={onClose}
          />
        ) : currentCollector ? (
          <CollectorCreateStep
            key={`${currentPosition}-${currentCollector.collNr}`}
            projectId={projectId}
            payload={payload}
            collector={currentCollector}
            position={currentPosition + 1}
            total={selectedIndexes.length}
            collectors={collectors}
            pickings={pickings}
            lockedPickingId={lockedPickingId}
            defaultPickingId={wizardPickingId}
            existingBloks={existingBloks}
            existingVerdiepingen={existingVerdiepingen}
            onSaved={handleSaved}
            onSkip={handleSaved}
          />
        ) : null}
      </div>
    </div>
  );
}

function CollectorChecklist({
  collectors,
  selected,
  onToggle,
  onStart,
  onClose,
}: {
  collectors: CadCollectorLine[];
  selected: Set<number>;
  onToggle: (index: number) => void;
  onStart: () => void;
  onClose: () => void;
}) {
  return (
    <div className="p-5">
      <div className="max-h-[58vh] overflow-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wide text-td-muted">
            <tr>
              <th className="px-3 py-2 w-10"></th>
              <th className="px-3 py-2">Collector</th>
              <th className="px-3 py-2 text-right">m²</th>
              <th className="px-3 py-2 text-right">Kringen</th>
              <th className="px-3 py-2">Appartement</th>
            </tr>
          </thead>
          <tbody>
            {collectors.map((collector, index) => {
              const gaps = findKringGaps(collector.kringen.map((k) => k.kringNr));
              return (
                <tr key={`${collector.collNr}-${index}`} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(index)}
                      onChange={() => onToggle(index)}
                      className="h-4 w-4 rounded border-gray-300 text-td-blue focus:ring-td-blue/40"
                      aria-label={`Collector ${collector.collNr} selecteren`}
                    />
                  </td>
                  <td className="px-3 py-2 font-semibold text-td-navy">{defaultCollectorName(collector.collNr, collector.appartement)}</td>
                  <td className="px-3 py-2 text-right text-td-navy">{collector.m2}</td>
                  <td className="px-3 py-2 text-right">
                    {gaps.length > 0 ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-red-700" title={formatKringGaps(gaps)}>
                        {'⚠'} {collector.kringen.length}
                      </span>
                    ) : (
                      <span className="text-td-navy">{collector.kringen.length}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-td-muted">{collector.appartement || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-td-muted hover:text-td-navy">
          Annuleren
        </button>
        <button
          type="button"
          onClick={onStart}
          disabled={selected.size === 0}
          className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50"
        >
          Volgende
        </button>
      </div>
    </div>
  );
}

function CollectorCreateStep({
  projectId,
  payload,
  collector,
  position,
  total,
  collectors,
  pickings,
  lockedPickingId,
  defaultPickingId,
  existingBloks,
  existingVerdiepingen,
  onSaved,
  onSkip,
}: {
  projectId: number;
  payload: CadJsonPayload;
  collector: CadCollectorLine;
  position: number;
  total: number;
  collectors: Collector[];
  pickings: CollectorPicking[];
  lockedPickingId: number | null;
  // Wizard-level materiaallijst chosen at the intro (no filename match) — pre-fills the
  // per-collector select for every collector but stays editable. null when not chosen.
  defaultPickingId: number | null;
  existingBloks: string[];
  existingVerdiepingen: string[];
  onSaved: () => void;
  onSkip: () => void;
}) {
  // Systeem options come from the Odoo-defined roster (stores/systems); "meerdere
  // systemen" is collector-only, so the kring dropdown uses the kring list.
  const collectorSystems = useCollectorSystems();
  const kringSystems = useKringSystems();
  const collectorCodes = useMemo(() => new Set(collectorSystems.map((s) => s.code)), [collectorSystems]);
  const kringCodes = useMemo(() => new Set(kringSystems.map((s) => s.code)), [kringSystems]);
  const mappedSysteem = mapCadSysteemType(payload.instellingen.systeemType);
  const [name, setName] = useState(defaultCollectorName(collector.collNr, collector.appartement));
  // collector.niveau arrives pre-normalised from the API; normalise again
  // defensively so the <select> always starts on a valid key (or '' → prompt).
  const [verdieping, setVerdieping] = useState(normaliseVerdieping(collector.niveau || ''));
  const [blok, setBlok] = useState('');
  const [m2, setM2] = useState(String(collector.m2 || ''));
  const [systeem, setSysteem] = useState<CadSysteemEnum>(mappedSysteem);
  const [legpatroon, setLegpatroon] = useState(payload.instellingen.legpatroon || '10');
  const [materiaallijstId, setMateriaallijstId] = useState<number | null>(lockedPickingId ?? defaultPickingId);
  const [kringRows, setKringRows] = useState(() => collector.kringen.map((kring) => ({
    x_kringnummer: String(kring.kringNr),
    // Per-kring system from the CAD export (a collector can mix systems — e.g. staalnet + warp).
    x_system: initialKringSysteem(kring.systeem, mappedSysteem),
    x_legpatroon: extractLpNumber(kring.legpatroon) || payload.instellingen.legpatroon || '10',
    x_length: kring.lengthM,
  })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleCollectorSysteemChange(next: CadSysteemEnum) {
    setSysteem(next);
    // Mirror CollectorEditForm: propagate to every kring UNLESS "meerdere systemen"
    if (next !== 'meerdere systemen') {
      setKringRows((prev) => prev.map((kring) => ({ ...kring, x_system: next })));
    }
  }

  function handleCollectorLegpatroonChange(next: string) {
    setLegpatroon(next);
    // Propagate to every kring UNLESS the special "meerdere" sentinel
    if (next !== 'meerdere') {
      setKringRows((prev) => prev.map((kring) => ({ ...kring, x_legpatroon: next })));
    }
  }

  function updateKring(index: number, field: 'x_system' | 'x_legpatroon' | 'x_length' | 'x_kringnummer', value: string) {
    setKringRows((prev) => prev.map((kring, i) => {
      if (i !== index) return kring;
      if (field === 'x_length') return { ...kring, x_length: Number(value.replace(',', '.')) || 0 };
      return { ...kring, [field]: value };
    }));
  }

  // Derive collector systeem from kringen: all same → that value, mixed → "meerdere systemen".
  // Mirrors the auto-derive behavior in CollectorenTab's edit form.
  const derivedSysteem = useMemo<CadSysteemEnum>(() => {
    const values = kringRows.map((k) => k.x_system).filter(Boolean);
    if (values.length === 0) return systeem;
    const unique = new Set(values);
    if (unique.size === 1) return values[0] as CadSysteemEnum;
    return 'meerdere systemen';
  }, [kringRows, systeem]);

  useEffect(() => {
    if (derivedSysteem !== systeem) setSysteem(derivedSysteem);
  }, [derivedSysteem, systeem]);

  // Mirror legpatroon: all same → that value, mixed → "meerdere"
  const derivedLegpatroon = useMemo<string>(() => {
    const values = kringRows.map((k) => String(k.x_legpatroon)).filter(Boolean);
    if (values.length === 0) return legpatroon;
    const unique = new Set(values);
    if (unique.size === 1) return values[0];
    return 'meerdere';
  }, [kringRows, legpatroon]);

  useEffect(() => {
    if (derivedLegpatroon !== legpatroon) setLegpatroon(derivedLegpatroon);
  }, [derivedLegpatroon, legpatroon]);

  const effectiveVerdieping = verdieping.trim();
  const effectiveBlok = blok.trim() || 'Hoofdgebouw';

  const duplicate = collectors.some((existing) => {
    const existingBlok = existing.x_studio_blok ? existing.x_studio_blok[1] : '';
    return collectorNameMatches(existing.name, collector.collNr)
      && String(existing.x_studio_verdieping || '').trim() === effectiveVerdieping
      && existingBlok.trim() === effectiveBlok;
  });

  async function handleSave() {
    if (!name.trim()) {
      setError('Naam is verplicht');
      return;
    }
    if (!effectiveVerdieping) {
      setError('Verdieping is verplicht');
      return;
    }
    // A kring whose CAD system wasn't recognised keeps its raw label (not a valid kring system).
    // Force the user to pick a real one before saving, rather than 422 on the server.
    // With an empty roster (the /api/systems fetch failed) we cannot tell valid from
    // invalid — skip the guard rather than block every save; the server then simply
    // drops an unknown x_system (collector-write.ts) as it did before this check.
    const invalidKring = kringCodes.size === 0
      ? undefined
      : kringRows.find((k) => !kringCodes.has(k.x_system));
    if (invalidKring) {
      setError(`Onbekend systeem "${invalidKring.x_system || '—'}" bij kring ${invalidKring.x_kringnummer} — kies een geldig systeem.`);
      return;
    }
    // Materiaallijsten exist for this project but none is auto-matched/locked —
    // force a deliberate pick rather than letting this collector save unlinked.
    if (pickings.length > 0 && lockedPickingId === null && !materiaallijstId) {
      setError('Kies een materiaallijst');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createCollector({
        project_id: projectId,
        name: name.trim(),
        verdieping: effectiveVerdieping,
        blok: effectiveBlok,
        collector_m2: Number(m2.replace(',', '.')) || 0,
        systeem,
        ...(materiaallijstId ? { materiaallijst_id: materiaallijstId } : {}),
        // Normalise a Belgian comma in the legpatroon (fallback / manual-entry paths) to a
        // dot so the backend's parseFloat reads decimals like 7,5 → 7.5 verbatim.
        kringen: kringRows.map((k) => ({ ...k, x_system: k.x_system as CadSysteemEnum, x_legpatroon: String(k.x_legpatroon).replace(',', '.') })),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Collector aanmaken mislukt');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h4 className="text-sm font-bold text-td-navy">Collector {position} van {total} — CollNr {collector.collNr}</h4>
        <button type="button" onClick={onSkip} className="text-sm font-semibold text-td-muted hover:text-td-navy">
          Overslaan
        </button>
      </div>

      {!collector.niveau && (
        <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Geen verdieping opgegeven in CAD JSON — kies hieronder de juiste verdieping.
        </div>
      )}
      {duplicate && (
        <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Bestaat al — opslaan maakt een tweede collector aan.
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" role="alert">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Naam</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Verdieping</label>
          <select value={verdieping} onChange={(e) => setVerdieping(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue">
            <option value="">— kies verdieping —</option>
            {[...new Set<string>([...VERDIEPING_KEYS, ...existingVerdiepingen])].map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Blok</label>
          <input value={blok} onChange={(e) => setBlok(e.target.value)} list="cad-import-bloks" placeholder="Hoofdgebouw" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue" />
          <datalist id="cad-import-bloks">
            {[...new Set(['Hoofdgebouw', ...existingBloks])].sort().map((value) => <option key={value} value={value} />)}
          </datalist>
          {collector.appartement && <p className="mt-1 text-xs text-td-muted">CAD-info: Appartement {collector.appartement}</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">m²</label>
          <input type="number" min="0" step="0.1" value={m2} onChange={(e) => setM2(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Systeem</label>
          <select value={systeem} onChange={(e) => handleCollectorSysteemChange(e.target.value as CadSysteemEnum)} aria-label="Collector systeem" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue">
            {!collectorCodes.has(systeem) && <option value={systeem}>{systeem || '—'}</option>}
            {collectorSystems.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Legpatroon</label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {['10', '15', '20'].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => handleCollectorLegpatroonChange(v)}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${legpatroon === v ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
              >
                {v}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                const value = prompt('Legpatroon (getal):');
                if (value && !Number.isNaN(Number(value)) && Number(value) > 0) {
                  handleCollectorLegpatroonChange(value);
                }
              }}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${!['10', '15', '20', 'meerdere'].includes(legpatroon) ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
            >
              {!['10', '15', '20', 'meerdere'].includes(legpatroon) ? legpatroon : 'Ander'}
            </button>
            <button
              type="button"
              onClick={() => handleCollectorLegpatroonChange('meerdere')}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${legpatroon === 'meerdere' ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
            >
              Meerdere
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs font-semibold text-td-muted mb-1">Materiaallijst</label>
        {lockedPickingId !== null ? (
          <div className="w-full rounded-lg border border-td-blue/40 bg-blue-50 px-3 py-2 text-sm text-td-navy flex items-center gap-2">
            <span className="text-td-blue font-bold text-xs">✓</span>
            {(() => {
              const locked = pickings.find(p => p.id === lockedPickingId);
              return locked ? formatPickingOption(locked) : `#${lockedPickingId}`;
            })()}
          </div>
        ) : (
          <select
            value={materiaallijstId ?? ''}
            onChange={(e) => setMateriaallijstId(Number(e.target.value) || null)}
            required={pickings.length > 0}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-td-blue"
          >
            <option value="" disabled={pickings.length > 0}>
              {pickings.length > 0 ? 'Kies een materiaallijst...' : 'Geen materiaallijst beschikbaar'}
            </option>
            {pickings.map((picking) => (
              <option key={picking.id} value={picking.id}>{formatPickingOption(picking)}</option>
            ))}
          </select>
        )}
      </div>

      <div className="mt-5 rounded-lg border border-gray-200 overflow-auto max-h-[26vh]">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wide text-td-muted">
            <tr>
              <th className="px-2 py-2 w-20">#</th>
              <th className="px-2 py-2">Systeem</th>
              <th className="px-2 py-2 w-28">Legpatroon</th>
              <th className="px-2 py-2 w-28 text-right">Lengte</th>
            </tr>
          </thead>
          <tbody>
            {kringRows.map((kring, index) => (
              <tr key={`${kring.x_kringnummer}-${index}`} className="border-t border-gray-100">
                <td className="px-2 py-1">
                  <input
                    type="number"
                    value={kring.x_kringnummer}
                    onChange={(e) => updateKring(index, 'x_kringnummer', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue"
                    aria-label={`Kringnummer kring ${index + 1}`}
                  />
                </td>
                <td className="px-2 py-1">
                  <select
                    value={kring.x_system}
                    onChange={(e) => updateKring(index, 'x_system', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue bg-white"
                    aria-label={`Systeem kring ${index + 1}`}
                  >
                    {/* An unrecognised CAD label stays visible (and invalid) so the
                        user is forced to pick a real system — ADR-061 §3. */}
                    {!kringCodes.has(kring.x_system) && (
                      <option value={kring.x_system}>{kring.x_system || '—'}</option>
                    )}
                    {kringSystems.map((s) => (
                      <option key={s.code} value={s.code}>{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="any"
                    value={kring.x_legpatroon}
                    onChange={(e) => updateKring(index, 'x_legpatroon', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue"
                    aria-label={`Legpatroon kring ${index + 1}`}
                  />
                </td>
                <td className="px-2 py-1 text-right">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={kring.x_length}
                    onChange={(e) => updateKring(index, 'x_length', e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-td-blue text-right"
                    aria-label={`Lengte kring ${index + 1}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <button type="button" onClick={handleSave} disabled={saving} className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50">
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>
    </div>
  );
}

// Seed the collector name from CAD data as "{apartment} — Collector {n}".
//
// The CAD CollNr embeds the apartment plus a trailing " - {n}" collector-sequence
// suffix (e.g. "App D.1.1 - 1", "App D.5.1 (duplex) - 2"). The older format used a
// bare numeric CollNr ("1") with the apartment supplied separately. Both collapse to
// the same readable shape here — apartment first, then the collector sequence number —
// instead of the old "Collector {collNr} - {appartement}" which duplicated the
// apartment when it was already part of CollNr.
export function defaultCollectorName(collNr: string, appartement: string): string {
  const coll = collNr.trim();
  const apt = appartement.trim();

  // Split a trailing " - {n}" collector-sequence suffix off CollNr; a bare numeric
  // CollNr is itself the sequence (legacy format); otherwise CollNr embeds only the
  // apartment with no sequence.
  let embeddedApt: string;
  let seq: string;
  const seqMatch = coll.match(/^(.*?)\s*-\s*(\d+)\s*$/);
  if (seqMatch) {
    embeddedApt = seqMatch[1].trim();
    seq = seqMatch[2];
  } else if (/^\d+$/.test(coll)) {
    embeddedApt = '';
    seq = coll;
  } else {
    embeddedApt = coll;
    seq = '';
  }

  // Prefer the explicit Appartement field; fall back to the apartment embedded in CollNr.
  const aptLabel = apt || embeddedApt;

  if (aptLabel && seq) return `${aptLabel} — Collector ${seq}`;
  if (aptLabel) return aptLabel;
  if (seq) return `Collector ${seq}`;
  return coll ? `Collector ${coll}` : 'Collector';
}

function collectorNameMatches(name: string, collNr: string): boolean {
  return name.trim().toLowerCase().includes(collNr.trim().toLowerCase());
}

function extractLpNumber(legafstand: string): string {
  // Legafstanden can be decimal (7.5 / 12.5 cm) and CAD exports may use a Belgian
  // comma ("7,5"). Capture the whole number and normalise to a dot so parseFloat
  // on the backend reads it verbatim (a bare \d+ would truncate 7.5 → 7).
  const match = legafstand.match(/\d+(?:[.,]\d+)?/);
  return match ? match[0].replace(',', '.') : '';
}

// Initial system for an imported kring. A recognised CAD system maps to its portal value; an
// UNRECOGNISED one maps to 'meerdere systemen' (not a valid kring value) — so we keep the raw
// CAD label instead, which the per-kring dropdown surfaces via its unknown-value guard option,
// forcing a deliberate manual pick rather than silently saving the wrong system. Empty CAD
// system → fall back to the collector-level systeemType.
function initialKringSysteem(rawSysteem: string, collectorFallback: string): string {
  if (rawSysteem) {
    const mapped = mapCadSysteemType(rawSysteem);
    return mapped === 'meerdere systemen' ? rawSysteem : mapped;
  }
  // No per-kring system → inherit the collector-level systeem, but NEVER the collector-only
  // 'meerdere systemen' label (it isn't a valid kring value — happens when the CAD
  // SysteemType itself is unknown). Leave blank so the kring dropdown forces a real pick.
  return collectorFallback === 'meerdere systemen' ? '' : collectorFallback;
}
